import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, goToCheckout, fmtDateLong, fmtTimeRange, checkoutOriginUrl } from "../api";
import { isNative } from "../mobileAuth";
import { Page, SectionTitle } from "../components/Layout";
import { CalendarClock, ArrowLeft } from "lucide-react";

const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

// Salva l'attribuzione docente così sopravvive al checkout Stripe / ritorno.
function persistTeacher(cid, t) {
  if (t) localStorage.setItem(`mas_course_t_${cid}`, t);
}
function getTeacher(cid) {
  return localStorage.getItem(`mas_course_t_${cid}`) || "";
}

function CourseCard({ c, onClick }) {
  return (
    <button onClick={onClick} data-testid={`course-${c.id}`} className="w-full text-left card overflow-hidden">
      {c.image && <img src={c.image} alt={c.title} loading="lazy" className="w-full h-36 object-cover" />}
      <div className="p-4">
        <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">{c.category} · {fmtDateLong(c.date)}</div>
        <div className="serif text-lg mt-1">{c.title}</div>
        {c.teacher && <div className="text-sm text-gray-600">Docente: {c.teacher}</div>}
        <div className="flex items-center justify-between mt-2">
          <span className="font-semibold">{c.price > 0 ? fmtEur(c.price) : "Gratuito"}</span>
          <span className={`text-xs ${c.seats_left > 0 ? "text-gray-500" : "text-red-600 font-semibold"}`}>
            {c.seats_left > 0 ? `${c.seats_left} posti` : "Esaurito"}
          </span>
        </div>
      </div>
    </button>
  );
}

function CourseDetail({ cid }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [c, setC] = useState(null);
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false); // lock sincrono anti-doppio-submit (immune al batching di React)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = "w-full border border-gray-200 rounded-xl px-4 py-3 mt-1 focus:border-violet-500 outline-none";

  useEffect(() => {
    const t = params.get("t");
    if (t) persistTeacher(cid, t);
    api.get(`/courses/${cid}`).then((r) => setC(r.data)).catch(() => setC(false));
    api.post(`/courses/${cid}/track${t ? `?t=${encodeURIComponent(t)}` : ""}`).catch(() => {});
  }, [cid]); // eslint-disable-line

  if (c === false) return <Page><div className="text-center pt-20"><h1 className="serif text-2xl">Corso non trovato</h1><button onClick={() => nav("/corsi")} className="btn-primary mt-4">Tutti i corsi</button></div></Page>;
  if (!c) return <Page><p className="text-sm text-gray-400 pt-10">Caricamento…</p></Page>;

  const submit = async (e) => {
    e.preventDefault();
    if (busyRef.current) return; // lock sincrono: blocca click+Enter+doppio tap nello stesso tick
    if (!f.name.trim() || !f.phone.trim()) return toast.error("Inserisci nome e telefono.");
    busyRef.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${cid}/enroll`, {
        name: f.name, email: f.email, phone: f.phone,
        teacher_code: getTeacher(cid), origin_url: checkoutOriginUrl(), native: isNative(),
      });
      if (data.free) { toast.success("Iscrizione confermata!"); nav("/corsi"); return; }
      // Redirect top-level a Stripe: il pulsante resta bloccato (non riabilitiamo il lock).
      goToCheckout(data.checkout_url);
    } catch (er) {
      toast.error(apiErr(er));
      busyRef.current = false;
      setBusy(false); // riabilita solo in caso di errore recuperabile
    }
  };

  const soldOut = c.seats_left <= 0;
  return (
    <Page>
      <button onClick={() => nav("/corsi")} className="inline-flex items-center gap-1 text-sm text-gray-500 mb-3" data-testid="course-back"><ArrowLeft size={16} />Tutti i corsi</button>
      {c.image && <img src={c.image} alt={c.title} className="w-full h-48 object-cover rounded-2xl mb-4" />}
      <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">{c.category} · {fmtDateLong(c.date)}{c.time ? ` · ${fmtTimeRange(c.time)}` : ""}</div>
      <h1 className="serif text-2xl mt-1">{c.title}</h1>
      {c.teacher && <div className="text-sm text-gray-600 mt-1">Docente: {c.teacher}</div>}
      <p className="text-sm text-gray-700 mt-3 whitespace-pre-line">{c.description}</p>
      <div className="flex items-center justify-between mt-4 card p-4">
        <span className="text-xl font-bold">{c.price > 0 ? fmtEur(c.price) : "Gratuito"}</span>
        <span className={`text-sm ${soldOut ? "text-red-600 font-semibold" : "text-gray-500"}`}>{soldOut ? "Posti esauriti" : `${c.seats_left} posti disponibili`}</span>
      </div>

      {!soldOut ? (
        <form onSubmit={submit} className="mt-5">
          <div className="text-sm font-semibold mb-2">Iscriviti</div>
          <label className="block text-sm">Nome e cognome<input data-testid="enroll-name" className={inp} value={f.name} onChange={set("name")} /></label>
          <label className="block text-sm mt-3">Telefono<input data-testid="enroll-phone" type="tel" className={inp} value={f.phone} onChange={set("phone")} /></label>
          <label className="block text-sm mt-3">Email (opzionale)<input data-testid="enroll-email" type="email" className={inp} value={f.email} onChange={set("email")} /></label>
          <button data-testid="enroll-submit" disabled={busy} aria-busy={busy} className="btn-primary w-full mt-5 disabled:opacity-60 disabled:cursor-not-allowed">
            {busy ? (c.price > 0 ? "Reindirizzamento al pagamento…" : "Attendere…") : c.price > 0 ? `Iscriviti e paga ${fmtEur(c.price)}` : "Iscriviti gratuitamente"}
          </button>
          {c.price > 0 && <p className="text-[11px] text-gray-400 mt-2">Pagamento sicuro via Stripe. Metodi come Klarna possono comparire se disponibili. Rimborso: ≥10 giorni 80% · &lt;10 giorni ma ≥72 ore 50% · &lt;72 ore 0%.</p>}
        </form>
      ) : (
        <div className="card p-4 mt-5 text-sm text-gray-500" data-testid="course-soldout">Il corso ha raggiunto il numero massimo di iscritti.</div>
      )}
    </Page>
  );
}

export default function CorsiPage() {
  const { cid } = useParams();
  const [courses, setCourses] = useState(null);
  useEffect(() => { if (!cid) api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([])); }, [cid]);
  const nav = useNavigate();

  if (cid) return <CourseDetail cid={cid} />;

  return (
    <Page>
      <SectionTitle sub="Corsi, masterclass ed eventi da MAS.">Corsi & Eventi</SectionTitle>
      {courses === null && <p className="text-sm text-gray-400">Caricamento…</p>}
      {courses && courses.length === 0 && (
        <div className="card p-5 border-l-4 border-l-amber-400" data-testid="corsi-empty">
          <div className="flex items-center gap-2 text-amber-600 font-semibold text-sm"><CalendarClock size={18} />Nessun corso in programma</div>
          <p className="text-sm text-gray-600 mt-2">Al momento non ci sono corsi attivi. Torna presto per scoprire le prossime date.</p>
        </div>
      )}
      <div className="space-y-4">
        {(courses || []).map((c) => <CourseCard key={c.id} c={c} onClick={() => nav(`/corsi/${c.id}`)} />)}
      </div>
    </Page>
  );
}
